import { z } from 'zod';
const define = (spec) => spec;
export const mediaCommands = [
    'pause',
    'resume',
    'toggle',
    'next',
    'previous',
    'mute',
    'volume_up',
    'volume_down',
];
export const spotifyCommands = [
    'pause',
    'resume',
    'next',
    'previous',
    'volume_up',
    'volume_down',
];
/** Apps the desktop agent knows how to start. Anything else is refused. */
export const knownApps = ['discord', 'steam', 'spotify', 'browser', 'obs', 'explorer'];
/** Which TV, when the agent has more than one (names from its ELENA_TVS). */
const tvParam = z
    .string()
    .trim()
    .min(1)
    .max(40)
    .optional()
    .describe('Koji TV: ime sobe sa spiska TV-ova (npr. "Moja soba"); bez njega ide na prvi TV');
export const actions = {
    'youtube.play': define({
        id: 'youtube.play',
        description: 'Pusti pesmu, izvođača ili plejlistu sa YouTube-a na računaru.',
        target: 'pc',
        risk: 'low',
        params: z.object({
            query: z.string().min(1).max(200).describe('Šta da pusti, npr. "Ceca Beograd"'),
        }),
    }),
    'media.control': define({
        id: 'media.control',
        description: 'Kontrola muzike i zvuka na računaru: pauza, nastavi, sledeća, prethodna, jačina.',
        target: 'pc',
        risk: 'low',
        params: z.object({ command: z.enum(mediaCommands).describe('Šta da uradi') }),
    }),
    'fivem.launch': define({
        id: 'fivem.launch',
        description: 'Pokreni FiveM na računaru, bez povezivanja na server.',
        target: 'pc',
        risk: 'low',
        params: z.object({}),
    }),
    'fivem.connect': define({
        id: 'fivem.connect',
        description: 'Pokreni FiveM i poveži se na podešeni server (podrazumevano MILPET).',
        target: 'pc',
        risk: 'low',
        params: z.object({
            server: z
                .string()
                .max(40)
                .optional()
                .describe('Ime servera iz podešavanja; izostavi za podrazumevani'),
        }),
    }),
    'fivem.close': define({
        id: 'fivem.close',
        description: 'Ugasi FiveM na računaru: zatvara sve FiveM procese (igru, launcher i pozadinske procese).',
        target: 'pc',
        risk: 'medium',
        params: z.object({}),
    }),
    'elena.cleanup': define({
        id: 'elena.cleanup',
        description: 'Počisti duple Elene na računaru: ugasi višak Elena agenata i „Hej Elena“ prozora, ostavi jednog agenta i jedan prozor. Kad kaže da je upaljeno više Elena ili da se javlja duplo.',
        target: 'pc',
        risk: 'low',
        params: z.object({}),
    }),
    'app.open': define({
        id: 'app.open',
        description: 'Otvori aplikaciju na računaru (discord, steam, spotify, browser, obs, explorer).',
        target: 'pc',
        risk: 'low',
        params: z.object({ app: z.enum(knownApps).describe('Aplikacija') }),
    }),
    'url.open': define({
        id: 'url.open',
        description: 'Otvori web stranicu u browseru na računaru. Samo https adrese.',
        target: 'pc',
        risk: 'medium',
        params: z.object({
            url: z
                .string()
                .url()
                .max(500)
                .refine((u) => u.startsWith('https://'), 'Dozvoljene su samo https adrese')
                .describe('Adresa stranice'),
        }),
    }),
    'system.lock': define({
        id: 'system.lock',
        description: 'Zaključaj računar.',
        target: 'pc',
        risk: 'medium',
        params: z.object({}),
    }),
    'system.shutdown': define({
        id: 'system.shutdown',
        description: 'Ugasi računar posle zadatog broja sekundi.',
        target: 'pc',
        risk: 'high',
        params: z.object({
            delaySeconds: z.number().int().min(10).max(3600).default(60).describe('Za koliko sekundi'),
        }),
    }),
    'tv.power': define({
        id: 'tv.power',
        description: 'Upali ili ugasi LG televizor.',
        target: 'pc',
        risk: 'low',
        params: z.object({
            tv: tvParam,
            state: z.enum(['on', 'off']).describe('on upali, off ugasi'),
        }),
    }),
    'tv.app': define({
        id: 'tv.app',
        description: 'Otvori aplikaciju na LG televizoru (Netflix, YouTube, Disney+, HBO Max, Prime Video, Spotify, Twitch, browser, TV kanali…). Upali TV ako je ugašen.',
        target: 'pc',
        risk: 'low',
        params: z.object({
            tv: tvParam,
            app: z
                .string()
                .regex(/^[\p{L}\p{N} .+&'!-]{1,40}$/u, 'Ime aplikacije')
                .describe('Ime aplikacije, npr. "Netflix"'),
        }),
    }),
    'tv.youtube': define({
        id: 'tv.youtube',
        description: 'Pusti pesmu ili video sa YouTube-a na LG televizoru.',
        target: 'pc',
        risk: 'low',
        params: z.object({
            tv: tvParam,
            query: z.string().min(1).max(200).describe('Šta da pusti, npr. "Ceca Beograd"'),
        }),
    }),
    'tv.control': define({
        id: 'tv.control',
        description: 'Kontrola LG televizora: jačina (gore, dole, tačan nivo), zvuk isključi/uključi, pauza, play, stop, ekran ugasi/upali (zvuk ostaje).',
        target: 'pc',
        risk: 'low',
        params: z.object({
            tv: tvParam,
            command: z
                .enum([
                'volume_up',
                'volume_down',
                'set_volume',
                'mute',
                'unmute',
                'pause',
                'play',
                'stop',
                'screen_off',
                'screen_on',
            ])
                .describe('Šta da uradi'),
            level: z
                .number()
                .int()
                .min(0)
                .max(100)
                .optional()
                .describe('Jačina 0-100, samo za set_volume'),
        }),
    }),
    'tv.input': define({
        id: 'tv.input',
        description: 'Prebaci LG televizor na HDMI ulaz (npr. PS5).',
        target: 'pc',
        risk: 'low',
        params: z.object({
            tv: tvParam,
            hdmi: z
                .number()
                .int()
                .min(1, 'HDMI ulaz je 1 do 4')
                .max(4, 'HDMI ulaz je 1 do 4')
                .describe('Broj HDMI ulaza'),
        }),
    }),
    'tv.notify': define({
        id: 'tv.notify',
        description: 'Prikaži kratku poruku na ekranu LG televizora.',
        target: 'pc',
        risk: 'low',
        params: z.object({
            tv: tvParam,
            text: z.string().min(1).max(200).describe('Poruka'),
        }),
    }),
    'ps5.power': define({
        id: 'ps5.power',
        description: 'Upali PS5 iz režima mirovanja (i prebaci TV na njegov HDMI ako je podešeno) ili ga vrati u mirovanje.',
        target: 'pc',
        risk: 'low',
        params: z.object({ state: z.enum(['on', 'off']).describe('on upali, off mirovanje') }),
    }),
    'ps5.status': define({
        id: 'ps5.status',
        description: 'Proveri da li je PS5 upaljen ili u mirovanju.',
        target: 'pc',
        risk: 'low',
        params: z.object({}),
    }),
    'routine.run': define({
        id: 'routine.run',
        description: 'Pokreni sačuvanu rutinu po imenu (npr. „Veče za igru“).',
        target: 'server',
        risk: 'low',
        params: z.object({ name: z.string().min(1).max(60).describe('Ime rutine') }),
    }),
    'phone.notify': define({
        id: 'phone.notify',
        description: 'Pošalji notifikaciju na telefon (radi i kad je Elena aplikacija zatvorena). Za podsetnike i upozorenja.',
        target: 'server',
        risk: 'low',
        params: z.object({
            text: z.string().min(1).max(300).describe('Tekst notifikacije'),
            title: z.string().max(60).optional().describe('Naslov (podrazumevano „Elena“)'),
        }),
    }),
    'discord.send': define({
        id: 'discord.send',
        description: 'Pošalji poruku u Discord kanal preko webhooka iz Integracija. Bez @everyone i pingova.',
        target: 'server',
        risk: 'medium',
        params: z.object({
            text: z.string().min(1).max(1500).describe('Poruka'),
            channel: z
                .string()
                .max(40)
                .optional()
                .describe('Ime kanala iz Integracija; izostavi za prvi'),
        }),
    }),
    'spotify.play': define({
        id: 'spotify.play',
        description: 'Pusti pesmu, izvođača, album ili plejlistu na Spotify-u (na uređaju gde je Spotify otvoren).',
        target: 'server',
        risk: 'low',
        params: z.object({
            query: z.string().min(1).max(200).describe('Šta da pusti, npr. "Ceca Fazoni"'),
            kind: z
                .enum(['track', 'artist', 'album', 'playlist'])
                .default('track')
                .describe('Pesma, izvođač, album ili plejlista'),
        }),
    }),
    'spotify.control': define({
        id: 'spotify.control',
        description: 'Kontrola Spotify-a: pauza, nastavi, sledeća, prethodna, jačina.',
        target: 'server',
        risk: 'low',
        params: z.object({ command: z.enum(spotifyCommands).describe('Šta da uradi') }),
    }),
    'reminder.set': define({
        id: 'reminder.set',
        description: 'Zakaži podsetnik: stiže kao notifikacija na telefon i Elena ga kaže naglas. Zadaj tačno jedno: inMinutes ili at.',
        target: 'server',
        risk: 'low',
        params: z.object({
            text: z.string().min(1).max(200).describe('Na šta da podseti, npr. "restartuj server"'),
            inMinutes: z
                .number()
                .int()
                .min(1)
                .max(43_200)
                .optional()
                .describe('Za koliko minuta od sada'),
            at: z
                .string()
                .regex(/^(\d{4}-\d{2}-\d{2}T)?\d{2}:\d{2}$/, 'Vreme je HH:MM ili YYYY-MM-DDTHH:MM')
                .optional()
                .describe('Tačno vreme po Beogradu: "20:00" (sledeći put kad bude 20:00) ili "2026-10-18T09:30"'),
        }),
    }),
    'reminder.list': define({
        id: 'reminder.list',
        description: 'Prikaži zakazane podsetnike.',
        target: 'server',
        risk: 'low',
        params: z.object({}),
    }),
    'reminder.cancel': define({
        id: 'reminder.cancel',
        description: 'Otkaži podsetnik po delu teksta, ili sve sa "sve".',
        target: 'server',
        risk: 'low',
        params: z.object({
            match: z.string().min(1).max(100).describe('Deo teksta podsetnika, ili "sve"'),
        }),
    }),
    'server.stats': define({
        id: 'server.stats',
        description: 'Statistika FiveM servera: najviše igrača, prosek po satima, padovi, ko najviše igra i najbolje vreme za restart.',
        target: 'server',
        risk: 'low',
        params: z.object({
            days: z.number().int().min(1).max(14).default(7).describe('Za koliko poslednjih dana'),
        }),
    }),
    'fivem.restart_resource': define({
        id: 'fivem.restart_resource',
        description: 'Restartuj jedan resurs (skriptu) na FiveM serveru preko elena_bridge.',
        target: 'server',
        risk: 'medium',
        params: z.object({
            resource: z
                .string()
                .regex(/^[\w-]{1,64}$/, 'Ime resursa sme da ima slova, brojeve, - i _')
                .describe('Tačno ime resursa, npr. "imanje_system"'),
        }),
    }),
    'fivem.announce': define({
        id: 'fivem.announce',
        description: 'Pošalji obaveštenje svim igračima u igri (chat).',
        target: 'server',
        risk: 'medium',
        params: z.object({ text: z.string().min(1).max(200).describe('Tekst obaveštenja') }),
    }),
    'fivem.kick': define({
        id: 'fivem.kick',
        description: 'Izbaci igrača sa servera po ID-u.',
        target: 'server',
        risk: 'high',
        params: z.object({
            playerId: z.number().int().min(1).max(4096).describe('ID igrača na serveru'),
            reason: z.string().max(120).optional().describe('Razlog koji igrač vidi'),
        }),
    }),
    'fivem.errors': define({
        id: 'fivem.errors',
        description: 'Poslednje greške iz konzole FiveM servera, da objasniš šta je puklo.',
        target: 'server',
        risk: 'low',
        params: z.object({}),
    }),
    'fivem.resources': define({
        id: 'fivem.resources',
        description: 'Lista resursa na FiveM serveru i da li rade; opciono filter po imenu.',
        target: 'server',
        risk: 'low',
        params: z.object({
            filter: z.string().max(40).optional().describe('Deo imena resursa'),
        }),
    }),
    'fivem.restart_resources': define({
        id: 'fivem.restart_resources',
        description: 'Restartuj (ensure) više resursa odjednom na FiveM serveru. Zaštićeni (es_extended, oxmysql…) se preskaču.',
        target: 'server',
        risk: 'medium',
        params: z.object({
            resources: z
                .array(z.string().regex(/^[\w-]{1,64}$/, 'Ime resursa sme da ima slova, brojeve, - i _'))
                .min(1)
                .max(40)
                .describe('Tačna imena resursa'),
        }),
    }),
    'fivem.restart_changed': define({
        id: 'fivem.restart_changed',
        description: 'Restartuj resurse koje je promenio poslednji deploy servera iz OPS-a (za „restartuj promenjene resurse“, „ensure šta je novo“).',
        target: 'server',
        risk: 'medium',
        params: z.object({}),
    }),
    'fivem.restart_all': define({
        id: 'fivem.restart_all',
        description: 'Restartuj (ensure) SVE pokrenute resurse osim zaštićenih, bez gašenja servera. Samo kad korisnik izričito traži sve resurse; za ceo server koristi fivem_restart_server.',
        target: 'server',
        risk: 'high',
        params: z.object({}),
    }),
    'fivem.restart_server': define({
        id: 'fivem.restart_server',
        description: 'Restartuj ceo FiveM server: odbrojavanje igračima u chatu, svi se izbace (podaci se sačuvaju), server se ugasi, a txAdmin ga podigne.',
        target: 'server',
        risk: 'high',
        params: z.object({
            minutes: z
                .number()
                .min(0)
                .max(60)
                .default(5)
                .describe('Za koliko minuta; 0 znači za 10 sekundi'),
            reason: z
                .string()
                .max(120)
                .optional()
                .describe('Razlog koji igrači vide, npr. "Novi update"'),
        }),
    }),
    'fivem.restart_cancel': define({
        id: 'fivem.restart_cancel',
        description: 'Otkaži zakazani restart celog FiveM servera.',
        target: 'server',
        risk: 'medium',
        params: z.object({}),
    }),
    'fivem.recent_changes': define({
        id: 'fivem.recent_changes',
        description: 'Šta je skoro menjano na FiveM serveru: poslednji deployevi iz OPS-a, kada, sa koje grane i koji resursi su promenjeni.',
        target: 'server',
        risk: 'low',
        params: z.object({
            count: z.number().int().min(1).max(5).default(3).describe('Koliko poslednjih deployeva'),
        }),
    }),
    'ops.status': define({
        id: 'ops.status',
        description: 'Stanje OPS panela (MILPET VPS): koja grana admin panela radi, novi commitovi, servisi (api, expo, ops), auto deploy, šta je na FiveM serveru i da li čeka restart.',
        target: 'server',
        risk: 'low',
        params: z.object({}),
    }),
    'ops.branches': define({
        id: 'ops.branches',
        description: 'Najnovije git grane sa GitHub-a, sa poslednjim commitom, autorom i koliko su ispred produkcije. project "server" je FiveM gamemode, "admin" je milpet-admin panel.',
        target: 'server',
        risk: 'low',
        params: z.object({
            project: z
                .enum(['server', 'admin'])
                .describe('server = FiveM gamemode, admin = milpet-admin'),
            fetch: z.boolean().default(true).describe('Prvo osveži sa GitHub-a (fetch)'),
        }),
    }),
    'ops.server_plan': define({
        id: 'ops.server_plan',
        description: 'Pregled pre deploya na FiveM server: koliko commitova i fajlova bi grana poslala i koje resurse menja. Ništa ne šalje.',
        target: 'server',
        risk: 'low',
        params: z.object({
            branch: z
                .string()
                .regex(/^[\w./-]{1,100}$/, 'Neispravno ime grane')
                .describe('Grana gamemoda'),
        }),
    }),
    'ops.server_deploy': define({
        id: 'ops.server_deploy',
        description: 'Deployuj granu FiveM gamemoda na server (FTP, samo resources/, nikad server.cfg). Server se ne restartuje sam; posle ponudi restart promenjenih resursa ili servera.',
        target: 'server',
        risk: 'high',
        params: z.object({
            branch: z
                .string()
                .regex(/^[\w./-]{1,100}$/, 'Neispravno ime grane')
                .describe('Grana, npr. "main"'),
        }),
    }),
    'ops.admin_deploy': define({
        id: 'ops.admin_deploy',
        description: 'Deployuj milpet-admin panel (pull, build, restart servisa). Bez grane deployuje trenutnu granu; sa granom prebacuje panel na nju.',
        target: 'server',
        risk: 'high',
        params: z.object({
            branch: z
                .string()
                .regex(/^[\w./-]{1,100}$/, 'Neispravno ime grane')
                .optional()
                .describe('Grana; izostavi za trenutnu'),
            migrate: z
                .boolean()
                .default(false)
                .describe('Pusti i nove migracije baze sa test grane (samo kad korisnik to izričito kaže)'),
        }),
    }),
    'ops.rollback': define({
        id: 'ops.rollback',
        description: 'Vrati poslednji deploy (rollback): "server" za FiveM gamemode, "admin" za milpet-admin.',
        target: 'server',
        risk: 'high',
        params: z.object({
            project: z
                .enum(['server', 'admin'])
                .describe('server = FiveM gamemode, admin = milpet-admin'),
        }),
    }),
    'ops.job': define({
        id: 'ops.job',
        description: 'Pokreni OPS posao: fetch (admin repo), game-fetch (gamemode repo), restart-running (restart servisa admina), install (pnpm install), build.',
        target: 'server',
        risk: 'medium',
        params: z.object({
            job: z.enum(['fetch', 'game-fetch', 'restart-running', 'install', 'build']).describe('Posao'),
        }),
    }),
    'ops.service': define({
        id: 'ops.service',
        description: 'Restartuj, pokreni ili zaustavi servis admin panela na VPS-u: api, expo ili ops.',
        target: 'server',
        risk: 'medium',
        params: z.object({
            service: z.enum(['api', 'expo', 'ops']).describe('Servis'),
            action: z.enum(['restart', 'start', 'stop']).describe('Šta da uradi'),
        }),
    }),
    'ops.jobs': define({
        id: 'ops.jobs',
        description: 'Istorija OPS poslova (deployevi, fetch, rollback): šta radi sada, šta je uspelo, a za neuspeli i kraj loga.',
        target: 'server',
        risk: 'low',
        params: z.object({}),
    }),
    'ops.logs': define({
        id: 'ops.logs',
        description: 'Greške iz logova na VPS-u: api, expo, ops, jobs (deployevi) ili caddy. Da objasniš šta je puklo.',
        target: 'server',
        risk: 'low',
        params: z.object({
            source: z.enum(['api', 'expo', 'ops', 'jobs', 'caddy']).default('api').describe('Koji log'),
            lines: z.number().int().min(10).max(500).default(200).describe('Koliko poslednjih linija'),
        }),
    }),
    'ops.autodeploy': define({
        id: 'ops.autodeploy',
        description: 'Uključi ili isključi auto deploy admin panela (deploy posle svakog pusha).',
        target: 'server',
        risk: 'medium',
        params: z.object({ enabled: z.boolean().describe('true uključi, false isključi') }),
    }),
    'elena.mood': define({
        id: 'elena.mood',
        description: 'Promeni Elenin ton: nezna (podrazumevano), sarkasticna, motivator, baka, narator, komentator.',
        target: 'server',
        risk: 'low',
        params: z.object({
            mood: z
                .enum(['nezna', 'sarkasticna', 'motivator', 'baka', 'narator', 'komentator'])
                .describe('Raspoloženje'),
        }),
    }),
    'elena.language': define({
        id: 'elena.language',
        description: 'Promeni jezik razgovora (i glas i prepoznavanje govora): sr srpski, en engleski, de nemački. Za "pričaj na engleskom", "speak Serbian", "sprich Deutsch".',
        target: 'server',
        risk: 'low',
        params: z.object({ language: z.enum(['sr', 'en', 'de']).describe('Jezik') }),
    }),
    'weather.get': define({
        id: 'weather.get',
        description: 'Vremenska prognoza za grad (danas ili do 6 dana unapred): temperatura, osećaj, kiša, vetar i savet šta da obuče (jakna, kišobran). Bez grada koristi kućni grad iz podešavanja.',
        target: 'server',
        risk: 'low',
        params: z.object({
            city: z.string().max(80).optional().describe('Grad, npr. "Novi Sad"; izostavi za kućni grad'),
            day: z.number().int().min(0).max(6).default(0).describe('0 danas, 1 sutra, 2 prekosutra … 6'),
        }),
    }),
    'calendar.list': define({
        id: 'calendar.list',
        description: 'Pročitaj događaje iz kalendara (iCloud/CalDAV) za dan ili nekoliko dana.',
        target: 'server',
        risk: 'low',
        params: z.object({
            day: z
                .string()
                .regex(/^\d{4}-\d{2}-\d{2}$/, 'Dan je YYYY-MM-DD')
                .optional()
                .describe('Prvi dan (YYYY-MM-DD, po Beogradu); izostavi za danas'),
            days: z.number().int().min(1).max(14).default(1).describe('Koliko dana od tog dana'),
        }),
    }),
    'calendar.add': define({
        id: 'calendar.add',
        description: 'Dodaj događaj u kalendar. Vreme po Beogradu. Za ceo dan zadaj samo datum. alertMinutes dodaje podsetnik pre događaja (na telefonu i od Elene).',
        target: 'server',
        risk: 'low',
        params: z.object({
            title: z.string().min(1).max(120).describe('Naziv, npr. "Zubar"'),
            start: z
                .string()
                .regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/, 'Početak je YYYY-MM-DDTHH:MM ili YYYY-MM-DD za ceo dan')
                .describe('Početak: "2026-10-20T17:30" ili "2026-10-20" za ceo dan'),
            durationMinutes: z
                .number()
                .int()
                .min(5)
                .max(24 * 60)
                .default(60)
                .describe('Trajanje u minutima'),
            location: z.string().max(120).optional().describe('Mesto'),
            alertMinutes: z
                .number()
                .int()
                .min(0)
                .max(7 * 24 * 60)
                .optional()
                .describe('Koliko minuta pre da podseti'),
        }),
    }),
    'calendar.delete': define({
        id: 'calendar.delete',
        description: 'Obriši događaj iz kalendara po delu naziva (u narednih 60 dana).',
        target: 'server',
        risk: 'medium',
        params: z.object({
            match: z.string().min(1).max(120).describe('Deo naziva događaja'),
            day: z
                .string()
                .regex(/^\d{4}-\d{2}-\d{2}$/, 'Dan je YYYY-MM-DD')
                .optional()
                .describe('Dan događaja, ako ih ima više sa istim imenom'),
        }),
    }),
    'notes.add': define({
        id: 'notes.add',
        description: 'Zapiši belešku. Spisak (npr. "kupovina", "ideje", "filmovi") zadaj kad korisnik pomene spisak; inače "Beleške".',
        target: 'server',
        risk: 'low',
        params: z.object({
            text: z.string().min(1).max(1000).describe('Tekst beleške, kako ga je rekao'),
            list: z.string().max(40).optional().describe('Spisak, npr. "kupovina"'),
        }),
    }),
    'notes.read': define({
        id: 'notes.read',
        description: 'Pročitaj beleške: sve, jedan spisak ili one koje sadrže reč.',
        target: 'server',
        risk: 'low',
        params: z.object({
            list: z.string().max(40).optional().describe('Spisak'),
            match: z.string().max(100).optional().describe('Reč koju beleška sadrži'),
        }),
    }),
    'notes.delete': define({
        id: 'notes.delete',
        description: 'Obriši ili precrtaj beleške koje sadrže tekst ("kupio sam mleko" briše mleko sa spiska), ili ceo spisak sa "sve".',
        target: 'server',
        risk: 'low',
        params: z.object({
            match: z.string().min(1).max(100).describe('Deo teksta beleške, ili "sve"'),
            list: z.string().max(40).optional().describe('Spisak'),
        }),
    }),
    'memory.save': define({
        id: 'memory.save',
        description: 'Zapamti trajnu činjenicu o korisniku (ime, ljudi u životu, šta voli, navike, planovi). Kratko, u trećem licu.',
        target: 'server',
        risk: 'low',
        params: z.object({
            fact: z.string().min(3).max(200).describe('Npr. "Mama se zove Vesna"'),
        }),
    }),
    'memory.forget': define({
        id: 'memory.forget',
        description: 'Zaboravi zapamćene činjenice koje sadrže tekst, ili sve sa "sve".',
        target: 'server',
        risk: 'low',
        params: z.object({ match: z.string().min(1).max(100).describe('Deo činjenice ili "sve"') }),
    }),
    'music.find': define({
        id: 'music.find',
        description: 'Proveri koje pesme zaista postoje za zahtev (Spotify, iTunes, YouTube), pre nego što pustiš pesmu čiji naziv je možda pogrešno čut. Ne pušta ništa.',
        target: 'server',
        risk: 'low',
        params: z.object({
            query: z
                .string()
                .min(1)
                .max(200)
                .describe('Izvođač i naziv kako ih ti rekonstruišeš, npr. "David Guetta Turn Me On"'),
        }),
    }),
    'speech.learn': define({
        id: 'speech.learn',
        description: 'Nauči kako prepoznavanje govora pogrešno piše neku reč ili ime, da sledeći put razumeš (kad te korisnik ispravi).',
        target: 'server',
        risk: 'low',
        params: z.object({
            heard: z
                .string()
                .min(1)
                .max(80)
                .describe('Kako je prepoznavanje napisalo, npr. "david geta"'),
            meant: z.string().min(1).max(80).describe('Šta je korisnik mislio, npr. "David Guetta"'),
        }),
    }),
    'speech.forget': define({
        id: 'speech.forget',
        description: 'Zaboravi naučene izgovore koji sadrže tekst, ili sve sa "sve".',
        target: 'server',
        risk: 'low',
        params: z.object({ match: z.string().min(1).max(80).describe('Deo reči ili "sve"') }),
    }),
    'server.status': define({
        id: 'server.status',
        description: 'Proveri FiveM server: da li radi, broj igrača i ko je unutra.',
        target: 'server',
        risk: 'low',
        params: z.object({}),
    }),
};
export const actionIds = Object.keys(actions);
export const isActionId = (value) => Object.prototype.hasOwnProperty.call(actions, value);
/** Validates params for an action; throws a ZodError with a readable message on failure. */
export function parseActionParams(id, params) {
    return actions[id].params.parse(params ?? {});
}
//# sourceMappingURL=actions.js.map